import React, { memo, useMemo } from 'react';
import { appInsight, collectProperties } from 'components/appInsight';
import { IconsEnum } from 'components/Icons';
import {
  ScommesseTableRow,
  ScommesseTableRowProps,
} from 'components/scommesseTable/scommesseTableRow/ScommesseTableRow';
import { format } from 'date-fns';
import { useAvvenimento, useAvvenimentoLink, useCompetitors, useInfoTipo } from 'features/sport/hooks';
import { getDateFnsLocale, useTranslation } from 'hooks/useLingUI';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { ItemWithTranslation } from 'types/swagger';
import { isTruthy, purgeNulls } from 'utility/functions';
import styles from './ScommesseTableRowConditional.module.scss';

export type ScommesseTableRowConditionalProps = ItemWithTranslation<{ title?: string; gruppo?: string }> & {
  hasBorderSide?: boolean;
  infoTipoScommessaKey: string | undefined;
  index?: number;
  avvenimentoKey?: string;
  hasMultipleLines?: boolean;
  hasHeaderScommessa?: boolean;
  children: React.ReactNode;
  showOnlyHours?: boolean;
  isEstesa?: boolean;
  iconSrc?: string;
};

export const ScommesseTableRowConditional = memo(function ScommesseTableRowConditional({
  title,
  index,
  gruppo,
  iconSrc,
  isEstesa,
  children,
  titleTrKey,
  gruppoTrKey,
  showOnlyHours = false,
  hasBorderSide,
  avvenimentoKey,
  hasMultipleLines,
  hasHeaderScommessa,
  infoTipoScommessaKey,
}: ScommesseTableRowConditionalProps) {
  const { t } = useTranslation();
  const avvenimento = useAvvenimento(avvenimentoKey);
  const competitors = useCompetitors(avvenimentoKey);
  const infoTipoScommessa = useInfoTipo(infoTipoScommessaKey);

  const { longUrl, shortUrl, first } = useAvvenimentoLink(avvenimentoKey);

  const { live, key } = avvenimento ?? {};

  const getFormattedDate = (dataOra: string) =>
    format(new Date(dataOra), `dd/MM '-' HH:mm`, { locale: getDateFnsLocale() });

  const widgetMemo: ScommesseTableRowProps['widget'] = useMemo(() => {
    if (avvenimento.live?.hasStreaming) {
      return [
        {
          id: IconsEnum.STREAMING,
          url: first ? shortUrl : longUrl,
        },
      ];
    }

    return [];
  }, [avvenimento, first, shortUrl, longUrl]);

  if (!avvenimento) {
    appInsight?.trackException({
      exception: new Error(`Avvenimento not found with key: ${avvenimentoKey}`),
      properties: collectProperties(true, {
        avvenimentoKey,
      }),
    });
    return null;
  }

  if (infoTipoScommessa?.tipoVisualizzazione === VisualizationEnum.Antepost) {
    return (
      <div className={styles.container} data-testid={key!} suppressHydrationWarning>
        {avvenimento.dataOra && (
          <span className={styles.dateHour} suppressHydrationWarning>
            {getFormattedDate(avvenimento.dataOra)}
          </span>
        )}
        <strong className={styles.title} suppressHydrationWarning>
          {t(avvenimento.descrizioneTrKey, avvenimento.descrizione)}&nbsp;
        </strong>
        {children}
      </div>
    );
  }

  const isLive = purgeNulls(live) !== undefined;

  return (
    <ScommesseTableRow
      title={title}
      testId={key}
      titleTrKey={titleTrKey}
      avvenimentoKey={avvenimento.key}
      hasMultipleLines={hasMultipleLines}
      showOnlyHours={isTruthy(showOnlyHours)}
      live={isLive}
      competitors={competitors}
      urlAvvenimento={`/${avvenimento.slug}`}
      providerID={avvenimento.externalProviderMatchId}
      viewAllItemsNumber={isLive ? avvenimento.numeroScommesse : undefined}
      hasHeader={hasHeaderScommessa && index === 0}
      hasBorderSide={hasBorderSide}
      widget={widgetMemo}
      isEstesa={isEstesa}
      gruppo={gruppo}
      gruppoTrKey={gruppoTrKey}
      iconSrc={iconSrc}
      infoTipoScommessaKey={infoTipoScommessaKey}
    >
      {children}
    </ScommesseTableRow>
  );
});